import { useEffect, useState } from 'react'

import type { TagGroup } from '../../../../services/product/product-data'
import { getPreviewHeaders } from '../../../../utils/api-headers'
import type {
  GroupedItem,
  ProductListConfig,
  ProductReplacementConfig
} from '../models'
import { fetchProductsData } from '../utils/products-service'
import { getReplacementProductCode } from '../utils/replacement-product'
import { fetchReplacementsData } from '../utils/replacements-service'

export type ProductListInitialData = {
  items: GroupedItem[]
  facets: TagGroup[]
  gcodes: string[]
}

interface UseProductDataParams {
  config: ProductListConfig['config']
  superTags: string | undefined
  groupBy: ProductListConfig['groupBy']
  initialData: ProductListInitialData | undefined
  productReplacementConfig?: ProductReplacementConfig
}

export const useProductData = ({
  config,
  superTags,
  groupBy,
  initialData,
  productReplacementConfig
}: UseProductDataParams) => {
  const effectiveInitialData = productReplacementConfig
    ? undefined
    : initialData
  const [data, setData] = useState(effectiveInitialData)

  useEffect(() => {
    if (!effectiveInitialData) {
      const fetchData = async () => {
        const customHeaders = getPreviewHeaders()

        if (productReplacementConfig) {
          const productId = getReplacementProductCode()
          if (!productId) {
            setData({ items: [], facets: [], gcodes: [] })
            return
          }
          const result = await fetchReplacementsData({
            config,
            productId,
            groupBy,
            customHeaders
          })
          if (result?.data) {
            const cleanedData = {
              ...result.data,
              gcodes: (result.data.gcodes || []).filter(Boolean) as string[]
            }
            setData(cleanedData)
          }
          return
        }

        const res = await fetchProductsData({
          config,
          superTags: superTags ?? '',
          groupBy,
          customHeaders
        })
        if (res?.data) {
          const cleaned = {
            ...res.data,
            gcodes: (res.data.gcodes || []).filter(Boolean) as string[]
          }
          setData(cleaned)
        }
      }
      fetchData()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { data, setData }
}
